import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "../lib/utils";

const Popover        = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor  = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "start", sideOffset = 4, ...props }, ref) => (
  /**
   * ⚠ Portaled into the FULLSCREEN element when there is one, not always into
   * the body.
   *
   * The browser renders only the fullscreen element's subtree. A popover
   * portaled to `document.body` while something else is fullscreen is therefore
   * not merely behind that element - it is not drawn at all, and the trigger
   * reads as a control that stopped working. No z-index can fix it; the CRM's
   * register map hit exactly this when its filter rail went fullscreen.
   *
   * Resolved at render, which is enough: Radix only mounts this subtree while
   * the popover is open, so the target is always read against the fullscreen
   * state in force when the panel actually appears. `undefined` keeps Radix's
   * own default (the body) for the ordinary case.
   */
  <PopoverPrimitive.Portal
    container={
      (typeof document !== "undefined" ? (document.fullscreenElement as HTMLElement | null) : null) ??
      undefined
    }
  >
    <PopoverPrimitive.Content
      ref={ref}
      data-slot="popover-content"
      align={align}
      sideOffset={sideOffset}
      className={cn(
        // Above the page layers, because a popover is normally the thing the
        // user opened LAST: it must sit over the dialog (z-[70], see dialog.tsx)
        // that raised it, and over an app's own menu layer - the CRM's is
        // z-index 97, which the old z-[80] opened behind, hiding a calendar's
        // month header under the menu row that opened it.
        //
        // A Select list sits HIGHER still (z-[210], see select.tsx), because it
        // can be opened from inside one of these panels. Raising a popover past
        // that number puts a form back on top of its own dropdown.
        //
        // Radix copies this value onto its own portal wrapper as an inline
        // style at mount, so it cannot be corrected from an app stylesheet
        // afterwards: an inline z-index wins over any rule, and the wrapper is
        // what actually stacks. It has to be right here.
        "z-[120] rounded-md border bg-card text-card-foreground shadow-floating p-3 outline-none",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
